/**
 * formModalSignal — a tiny global signal for "is any config-driven form modal
 * currently open?".
 *
 * Producer: `LeadFormEmbed` (#1179/#1232) — pushes on modal open, pops on close.
 * Consumer: the floating chat widget `VoiceWidgetConnected` — hides itself while
 * a form modal is open so it doesn't cover the full-screen form / its submit on
 * mobile (#1232 item 3).
 *
 * The two live in separate shadow roots / React roots but the same bundle, so a
 * plain module singleton is enough — no `window` events, no DOM coupling. Uses a
 * counter (not a boolean) so overlapping modals resolve correctly.
 */

let openCount = 0;
const listeners = new Set();

function emit() {
  const open = openCount > 0;
  listeners.forEach((cb) => {
    try {
      cb(open);
    } catch {
      // A listener throwing must not break sibling listeners or the caller.
    }
  });
}

/** Register that a form modal opened. Emits only on the 0→1 transition. */
export function pushFormModalOpen() {
  openCount += 1;
  if (openCount === 1) emit();
}

/** Register that a form modal closed. Emits only on the 1→0 transition. */
export function popFormModalOpen() {
  if (openCount === 0) return;
  openCount -= 1;
  if (openCount === 0) emit();
}

/** @returns {boolean} whether any form modal is currently open. */
export function isFormModalOpen() {
  return openCount > 0;
}

/**
 * Subscribe to open/closed changes.
 * @param {(open: boolean) => void} cb
 * @returns {() => void} unsubscribe
 */
export function subscribeFormModal(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Test-only: reset module state between specs. */
export function __resetFormModalSignal() {
  openCount = 0;
  listeners.clear();
}
